import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, User, Loader2, Sparkles } from 'lucide-react';
import ColorAmbient from '../components/ColorAmbient';
import RegistrationMark from '../components/RegistrationMark';
import { BlogHeroVisual } from '../components/blog/BlogDiagrams';
import { useBlogList } from '../hooks/useApi';

const BlogCard = ({ post }) => (
  <Link
    to={`/blog/${post.slug}`}
    className="group flex flex-col rounded-3xl overflow-hidden bg-white border border-slate-100 shadow-sm hover:shadow-2xl hover:-translate-y-1 btn-transition"
    data-testid={`blog-card-${post.slug}`}
  >
    <div className="relative">
      <BlogHeroVisual post={post} rounded="rounded-none" />
      <span className="absolute top-4 left-4 inline-flex items-center bg-white/95 backdrop-blur px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-widest text-[#0A2A63] shadow-sm">
        {post.audience}
      </span>
    </div>
    <div className="p-7 flex flex-col flex-1">
      <h2 className="text-xl md:text-2xl font-black text-[#050F2E] tracking-tight leading-snug group-hover:text-[#0A2A63]">
        {post.title}
      </h2>
      <p className="mt-3 text-slate-600 leading-relaxed flex-1">{post.excerpt}</p>
      <div className="mt-5 flex items-center justify-between">
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1.5 font-semibold"><User className="h-3.5 w-3.5" />{post.author}</span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{post.read_minutes} min</span>
        </div>
        <span className="inline-flex items-center gap-1 text-sm font-bold text-[#0A2A63] group-hover:translate-x-0.5 transition-transform">
          Read <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </div>
  </Link>
);

const BlogIndex = () => {
  const { data: posts, loading } = useBlogList();

  useEffect(() => {
    document.title = 'Blog · Insights for Print Operations | Proof Nation';
  }, []);

  return (
    <main className="pt-28 pb-24 bg-white min-h-screen relative overflow-hidden">
      <ColorAmbient variant="cool" intensity="medium" />
      <RegistrationMark className="absolute top-24 left-6 opacity-40" size={24} />
      <RegistrationMark className="absolute top-24 right-6 opacity-40" size={24} />

      <section className="container-pn relative">
        <div className="max-w-3xl">
          <p className="text-xs uppercase tracking-[0.3em] text-[#FF4E50] font-bold mb-3 inline-flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5" /> Insights
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#050F2E] tracking-tight leading-[1.02]">
            The Proof Nation{' '}
            <span className="pn-gradient-text">
              Blog
            </span>
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Practical guidance on color consistency, proofing, pilots and print operations. Proof Nation does not
            simply sell printing technology — Proof Nation makes it work for the operation.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-24 flex items-center justify-center gap-3 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" /> <span className="font-semibold">Loading articles…</span>
          </div>
        ) : (
          <div className="mt-14 grid gap-7 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-grid">
            {posts.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
};

export default BlogIndex;
